import { isMailchimpAccount } from '@wix/ambassador-emails-email-marketing-v1-fake-mailchimp-service-entity/http';
import { getAscendStatus } from '@wix/ambassador-promote-ascend-utils-service-v1-utils/http';
import { HttpClient } from '@wix/http-client';

import { createEntryPoint } from '#packages/apilib';
import { register as ascendRegister } from '#packages/ascend';
import constants from '#packages/constants';
import * as stateManagement from '#packages/stateManagement';

import promoteMenuPanel from './appMenu/panels/menuPanel/menuPanel';
import * as promoteSuperAppButton from './button/promoteSuperAppButton';
import { createPromoteApi } from './promotePublicApi';
import { PromoteApiKey } from './publicApiKey';
import { PromoteScope } from './scope';

const { setSiteUserPreferences } = stateManagement.userPreferences.actions;
const { getSiteUserPreferences } = stateManagement.userPreferences.selectors;

const PromoteEntryPoint = createEntryPoint({
  name: 'PromoteApi',
  Scope: PromoteScope,
  publicApi({ contributeApi }) {
    contributeApi(PromoteApiKey, createPromoteApi);
  },
  initialize: async (scope: PromoteScope) => {
    await scope.editorCoreAPI.hooks._dsIsReady.promise;
    await scope.editorCoreAPI.hooks.userPreferencesLoaded.promise;

    const httpClient = new HttpClient({
      getAppToken: () =>
        scope.editorAPI.dsRead.platform.getAppDataByApplicationId(
          constants.APPLICATIONS.META_SITE_APPLICATION_ID,
        )?.instance || '',
    });
    const [hideAscend, isMailchimpUser] = await Promise.all([
      fetchIsKillingAscend(httpClient),
      getIsMailchimpUser(scope, httpClient),
    ]);

    if (typeof scope.editorAPI.store?.dispatch !== 'function') {
      if (process.env.NODE_ENV === 'test') {
        // ignore in tests for now, to make tests logs more readable
        return;
      }

      // TODO: looks like some circular dependency issue with `scope.editorAPI`
      throw new Error('scope.editorAPI.store.dispatch is not a function');
    }

    scope.editorAPI.store.dispatch(
      stateManagement.ascend.actions.updateKillingAscend(hideAscend),
    );
    scope.store.setIsMailchimpUser(isMailchimpUser);

    scope.editorCoreAPI.hooks.initReady.promise.then(() => {
      if (hideAscend) {
        promoteSuperAppButton.register(scope.editorAPI);
      } else {
        ascendRegister(scope.editorAPI);
      }
    });
  },
});

async function fetchIsMailchimpUser(httpClient: HttpClient): Promise<boolean> {
  const { data } = await httpClient.request(isMailchimpAccount({}));
  return data.result;
}

async function getIsMailchimpUser(scope: PromoteScope, httpClient: HttpClient) {
  const key = 'promote.isMailchimpUser';
  const storedValue = getSiteUserPreferences<boolean>(key)(
    scope.editorAPI.store.getState(),
  );

  if (storedValue !== undefined) {
    return storedValue;
  }

  const isMailchimpUser = await fetchIsMailchimpUser(httpClient);
  scope.editorAPI.store.dispatch(setSiteUserPreferences(key, isMailchimpUser));
  return isMailchimpUser;
}

async function fetchIsKillingAscend(httpClient: HttpClient) {
  const { data } = await httpClient
    .request(getAscendStatus({}))
    .catch(() => ({ data: { killingAscend: false } }));

  return data.killingAscend || false;
}

export type { PromoteApi } from './promotePublicApi';

export { promoteMenuPanel as panel, PromoteEntryPoint };
